import utilColors from "../../styles/utilColors";
import { commonDataGridStyles } from "../../styles/DataGridStyles";

export const indexStyles = {
  showAllEnvsStack: {
    m: 0,
    gap: "8px",
  },
  showAllEnvsBox: (colors) => ({
    whiteSpace: "nowrap",
    fontSize: "12.5px",
    color: colors.ctlInk,
  }),
  mainBox: { mx: "5px" },
  filtersStack: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 1.5,
    mb: 1.5,
  },
  searchField: { width: "260px" },
  filterSelect: { minWidth: "180px" },
  envSummary: (colors) => ({
    ml: "auto",
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    fontSize: "13px",
    color: colors.grey[600],
    whiteSpace: "nowrap",
  }),
  envSummaryRunning: (colors) => ({ color: utilColors(colors).oliveGreen }),
  envSummaryFailed: (colors) => ({ color: utilColors(colors).bloodRed }),
  gridContainer: {
    mt: 2,
    height: "10vh",
    alignItems: "center",
  },
  // Pins the "Columns" picker to the table's own top-right corner, straddling
  // its border, so it reads as part of the table rather than the page header
  // or the filter row above it.
  columnPickerCorner: {
    position: "absolute",
    top: -14,
    right: 12,
    zIndex: 1,
  },
  // Shown in place of the status badge while a refresh is queued locally.
  refreshingPill: (colors) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 12px",
    borderRadius: "8px",
    backgroundColor: colors.warnBg,
    color: colors.warnFg,
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "16px",
    whiteSpace: "nowrap",
  }),
  dataGridBoxStyles: (colors) => ({
    height: "86.7vh",
    width: "99.2%",
    ...commonDataGridStyles(colors),
  }),
  diableGridActionItem: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  diableGridActionItemTypography: (colors) => ({
    color: colors.grey[500],
  }),
  detailsDrawerPaper: (colors, width = 1080) => ({
    // A definite width is needed so the two panes can be sized independently.
    width: `min(${width}px, 95vw)`,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.primary[700],
  }),
  detailsDrawerHeader: (colors) => ({
    px: 3,
    py: 1.4,
    backgroundColor: colors.grey[900],
    borderBottom: `1px solid ${colors.grey[800]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  }),
  detailsDrawerTitle: (colors) => ({
    fontSize: "15px",
    fontWeight: 700,
    color: colors.grey[100],
  }),
  detailsDrawerSubtitle: (colors) => ({
    fontSize: "12px",
    color: colors.grey[400],
  }),
  // Two panes: members take whatever width is left, schedules sit in a fixed
  // rail. At the drawer's own default (1080px) that leaves members exactly
  // 600px, same as before this was made resizable — the 1fr track only
  // starts mattering once the drawer is dragged wider.
  detailsDrawerBody: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 480px",
  },
  detailsDrawerMembersPane: {
    p: "22px 26px 24px",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  detailsDrawerSchedulePane: (colors) => ({
    p: "22px 26px 24px",
    minHeight: 0,
    overflowY: "auto",
    borderLeft: `1px solid ${colors.grey[800]}`,
    backgroundColor: colors.primary[600],
  }),
  detailsDrawerFooter: (colors) => ({
    px: 3,
    py: 1.75,
    borderTop: `1px solid ${colors.grey[800]}`,
    backgroundColor: colors.primary[600],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  }),
  detailsDrawerFooterNote: (colors) => ({
    fontSize: "11.5px",
    color: colors.grey[400],
  }),
  detailsDrawerFooterActions: {
    display: "flex",
    gap: 1,
  },
  detailsDrawerButton: {
    width: "fit-content",
    m: "0",
  },
  // Outlined secondary action; the label color is driven from here since the
  // Typography child is rendered by the caller.
  detailsDrawerCancelButton: (colors) => ({
    width: "fit-content",
    m: "0",
    backgroundColor: colors.primary[700],
    borderColor: colors.grey[800],
    "& .MuiTypography-root": { color: colors.grey[700] },
    "&:hover": {
      backgroundColor: colors.grey[900],
      borderColor: colors.grey[800],
    },
  }),
};

// All row actions share one trailing cell, left-aligned under the column header
// so the buttons line up down the column regardless of which actions a row
// qualifies for.
export const rowActionsStyles = {
  cell: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "6px",
  },
  // The state action and Refresh each get a fixed slot so they line up down the
  // column no matter which label the row's status produces.
  stateSlot: { width: "120px", flexShrink: 0 },
  refreshSlot: { width: "88px", flexShrink: 0 },
  button: { m: 0, width: "100%" },
  iconButton: (colors) => ({
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    color: colors.railMuted,
    "&:hover": {
      backgroundColor: colors.ctlHoverBg,
      color: colors.ctlInkHover,
    },
  }),
  // Relocated from the leading `details` column — kept visually distinct
  // (blue hover) from the neutral copy/kebab icons beside it.
  tuneIconButton: (colors) => ({
    width: "26px",
    height: "26px",
    borderRadius: "6px",
    color: colors.railMuted,
    "&:hover": {
      backgroundColor: colors.actionHoverBg,
      color: colors.navActive,
    },
  }),
  icon: { fontSize: "16px" },
  menuPaper: (colors) => ({
    minWidth: 176,
    marginTop: "4px",
    borderRadius: "6px",
    border: `1px solid ${colors.menuBorder}`,
    backgroundColor: colors.surface,
    boxShadow: colors.shadow,
    "& .MuiMenu-list": { padding: "4px" },
    "& .MuiMenuItem-root": {
      height: 32,
      minHeight: 32,
      padding: "0 10px",
      borderRadius: "4px",
      fontSize: "12.5px",
      color: colors.ctlInk,
      "&:hover": {
        backgroundColor: colors.ctlHoverBg,
        color: colors.ctlInkHover,
      },
    },
  }),
};

// The member rows and the add row live inside one bordered card, so the row
// widths are shared from here to keep the two aligned.
export const membersListStyles = {
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  titleRow: { pb: "16px", flexShrink: 0 },
  title: (colors) => ({
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: colors.grey[100],
  }),
  count: (colors) => ({
    color: colors.grey[400],
    fontSize: "11px",
    fontWeight: 500,
  }),
  searchField: (colors) => ({
    width: "210px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px",
      backgroundColor: colors.primary[600],
      fontSize: "12.5px",
    },
    "& .MuiOutlinedInput-input": { py: "7px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[800] },
  }),
  // A single card wraps the list and the add row, matching the reference.
  card: (colors) => ({
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${colors.grey[800]}`,
    borderRadius: "10px",
    overflow: "hidden",
  }),
  list: { flex: 1, minHeight: 0 },
  memberRow: (colors) => ({
    px: "14px",
    py: "11px",
    borderBottom: `1px solid ${colors.grey[900]}`,
    "&:hover": {
      backgroundColor: colors.grey[900],
    },
  }),
  avatar: (colors) => ({
    width: "28px",
    height: "28px",
    flexShrink: 0,
    fontSize: "10.5px",
    fontWeight: 500,
    backgroundColor: colors.blue[100],
    color: colors.blue[500],
  }),
  emailCell: { flexGrow: 1, minWidth: 0 },
  emailText: (colors) => ({
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.3,
    color: colors.grey[100],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  emailMeta: (colors) => ({
    fontSize: "11px",
    lineHeight: 1.3,
    color: colors.grey[400],
  }),
  emailField: { flexGrow: 1, minWidth: 0 },
  // Compact outline so the roles read as chips rather than full form controls.
  roleField: (colors) => ({
    width: "116px",
    flexShrink: 0,
    "& .MuiOutlinedInput-input": {
      fontSize: "11.5px",
      fontWeight: 500,
      letterSpacing: "0.02em",
      py: "5px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.grey[800],
      borderRadius: "7px",
    },
    "& .MuiSvgIcon-root": { fontSize: "16px" },
  }),
  addRoleField: (colors) => ({
    width: "94px",
    flexShrink: 0,
    "& .MuiOutlinedInput-input": { fontSize: "12px", py: "8px" },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.grey[800],
      borderRadius: "7px",
    },
  }),
  rowAction: { width: "28px", flexShrink: 0 },
  deleteAction: (colors) => ({
    width: "24px",
    height: "24px",
    flexShrink: 0,
    color: colors.grey[400],
    "&:hover": { color: colors.red[500] },
  }),
  emptyState: (colors) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.grey[400],
    fontSize: "12px",
    p: "24px",
  }),
  // Pinned to the bottom of the card, visually separated from the rows.
  addRow: (colors) => ({
    px: "14px",
    py: "12px",
    flexShrink: 0,
    borderTop: `1px solid ${colors.grey[800]}`,
    backgroundColor: colors.primary[600],
  }),
  addButton: (colors) => ({
    width: "fit-content",
    m: "0",
    px: "14px",
    borderRadius: "7px",
    backgroundColor: colors.blue[500],
    borderColor: colors.blue[500],
  }),
};

export const requestCPDialogStyles = {
  gridContainer: {
    padding: "7px",
    width: "50vw",
  },
  drawerPaper: (colors, width = 480) => ({
    width: `min(${width}px, 100vw)`,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.primary[700],
  }),
  drawerHeader: (colors) => ({
    px: 3,
    py: 2,
    backgroundColor: colors.grey[900],
    borderBottom: `1px solid ${colors.grey[800]}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 2,
    flexShrink: 0,
  }),
  drawerTitle: (colors) => ({
    fontSize: "15px",
    fontWeight: 700,
    color: colors.grey[100],
  }),
  drawerSubtitle: (colors) => ({
    fontSize: "12px",
    color: colors.grey[400],
  }),
  drawerBody: {
    flex: 1,
    overflowY: "auto",
    p: "20px 24px",
  },
  drawerFooter: (colors) => ({
    px: 3,
    py: 1.75,
    borderTop: `1px solid ${colors.grey[800]}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: 1,
    flexShrink: 0,
  }),
  drawerButton: {
    width: "fit-content",
    m: "0",
  },
  // Outlined secondary action; the label color is driven from here since the
  // Typography child is rendered by the caller.
  drawerCancelButton: (colors) => ({
    width: "fit-content",
    m: "0",
    backgroundColor: colors.primary[700],
    borderColor: colors.grey[800],
    "& .MuiTypography-root": { color: colors.grey[700] },
    "&:hover": {
      backgroundColor: colors.grey[900],
      borderColor: colors.grey[800],
    },
  }),
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
  },
  fieldLabel: (colors) => ({
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: colors.grey[700],
    mb: "5px",
  }),
  requiredMark: (colors) => ({
    color: colors.red[500],
  }),
  helperText: (colors) => ({
    fontSize: "11px",
    color: colors.grey[400],
    ml: 0,
    mt: "4px",
  }),
  errorText: (colors) => ({
    fontSize: "11px",
    color: colors.red[500],
    ml: 0,
    mt: "4px",
  }),
  section: (colors) => ({
    borderTop: `1px solid ${colors.grey[900]}`,
    pt: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }),
  sectionTitle: (colors) => ({
    fontSize: "12px",
    fontWeight: 600,
    color: colors.grey[300],
    textTransform: "uppercase",
    letterSpacing: "0.07em",
  }),
  infoBanner: (colors) => ({
    backgroundColor: colors.blue[100],
    border: `1px solid ${colors.blue[200]}`,
    borderRadius: "6px",
    p: "10px 14px",
    display: "flex",
    gap: "9px",
    alignItems: "flex-start",
  }),
  infoIcon: (colors) => ({
    color: colors.blue[500],
    fontSize: "16px",
    mt: "1px",
  }),
  infoText: (colors) => ({
    fontSize: "12px",
    color: colors.blue[700],
    lineHeight: 1.5,
  }),
  multiSelect: {
    width: "100%",
  },
  // Softer chips than the shared green default so they read as tags, not buttons.
  multiSelectChip: (colors) => ({
    height: "22px",
    borderRadius: "4px",
    bgcolor: colors.blue[100],
    opacity: 1,
    color: colors.blue[700],
    fontSize: "11px",
    fontWeight: 600,
    "& .MuiChip-deleteIcon": {
      fontSize: "14px",
      color: colors.blue[500],
      "&:hover": { color: colors.blue[700] },
    },
  }),
};
